// src/components/rpa/agent/RpaAgentChatLogic.js
//
// ── FIXED (Aug 2026) ─────────────────────────────────────────────────────────
// 1. handleAgentResponse had no case for chain results, so a "run all steps"
//    request fell through to `default` and rendered as a plain text blob.
//    BatchProgressCard was never mounted. Now handled by `case 'chain'`.
// 2. Live chain progress is emitted by the backend as 'rpa_agent_update' into
//    the doc:<docId> room. The socket effect only listened for `rpa_doc_<id>`,
//    so per-step ticks were dropped even once the backend emitter was fixed.
//    Now subscribed alongside the existing doc-update listener.
// 3. Chain runs surface their anomalies, grouped by the step that produced
//    them so the AuditIssue tags stay accurate.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FaRobot, FaPaperclip } from 'react-icons/fa';
import AgentApprovalCard    from './AgentApprovalCard';
import AgentResultCard      from './AgentResultCard';
import PBCLetterCard        from './PBCLetterCard';
import ManagementReportCard from './ManagementReportCard';
import AnomalyEscalationCard from './AnomalyEscalationCard';
import WorkflowStatusCard   from './WorkflowStatusCard';
import EngagementSetupCard  from './EngagementSetupCard';
import BatchProgressCard    from './BatchProgressCard';
import './RpaAgentChatLogic.css';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// Stable id for the transient "currently running" line during a chain.
const CHAIN_LIVE_ID = 'chain-live';

// Maps stepId → { endpoint, fieldName }
// fieldName MUST match the multer .single()/.array() field in each backend controller
const FILE_UPLOAD_ENDPOINTS = {
  upload_trial_balance:            { endpoint: '/admin/rpa/docs/{docId}/trial-balance/upload',           fieldName: 'trialBalance'            },
  upload_income_statement:         { endpoint: '/admin/rpa/docs/{docId}/income-statement/upload',         fieldName: 'incomeStatement'         },
  upload_bank_statements:          { endpoint: '/admin/rpa/docs/{docId}/bank-statements/upload',          fieldName: 'bankStatements'          },
  upload_bank_reconciliation:      { endpoint: '/admin/rpa/docs/{docId}/bank-reconciliation/upload',      fieldName: 'reconciliation'          },
  upload_bank_confirmations:       { endpoint: '/admin/rpa/docs/{docId}/bank-confirmations/upload',       fieldName: 'bankConfirmation'        },
  upload_ar_confirmations:         { endpoint: '/admin/rpa/docs/{docId}/ar-confirmations/upload',         fieldName: 'arConfirmation'          },
  upload_ap_confirmations:         { endpoint: '/admin/rpa/docs/{docId}/ap-confirmations/upload',         fieldName: 'apConfirmation'          },
  upload_inventory_confirmations:  { endpoint: '/admin/rpa/docs/{docId}/inventory-confirmations/upload',  fieldName: 'inventoryConfirmation'   },
  upload_inventory_observation:    { endpoint: '/admin/rpa/docs/{docId}/inventory-observation/upload',    fieldName: 'inventoryObservation'    },
  upload_supporting_documents:     { endpoint: '/admin/rpa/docs/{docId}/supporting/{docType}',              fieldName: 'supportingDocument',     needsDocType: true },
};

const SUPPORTING_DOC_TYPES = [
  { value: 'cash_flow_statement',            label: 'Cash Flow Statement'            },
  { value: 'statement_of_changes_equity',    label: 'Statement of Changes in Equity' },
  { value: 'notes_to_financial_statements',  label: 'Notes to Financial Statements'  },
  { value: 'general_ledger',                 label: 'General Ledger'                 },
  { value: 'ar_aging_report',                label: 'AR Aging Report'                },
  { value: 'vendor_statements',              label: 'Vendor Statements'              },
  { value: 'inventory_valuation_schedules',  label: 'Inventory Valuation Schedules'  },
  { value: 'fixed_asset_register',           label: 'Fixed Asset Register'           },
  { value: 'depreciation_schedules',         label: 'Depreciation Schedules'         },
  { value: 'debt_confirmations',             label: 'Debt Confirmations'             },
  { value: 'loan_agreements',                label: 'Loan Agreements'                },
  { value: 'lease_agreements',               label: 'Lease Agreements'               },
  { value: 'legal_documents',                label: 'Legal Documents'                },
  { value: 'tax_returns',                    label: 'Tax Returns'                    },
  { value: 'regulatory_filings',             label: 'Regulatory Filings'             },
  { value: 'management_representation_letter', label: 'Management Representation Letter' },
  { value: 'prior_year_financials',          label: 'Prior Year Financials'          },
  { value: 'supporting_schedules',           label: 'Supporting Schedules'           },
  { value: 'additional_evidence',            label: 'Additional Evidence'            },
];

const FILE_STEP_LABELS = {
  upload_trial_balance:            'Trial Balance',
  upload_income_statement:         'Income Statement',
  upload_bank_statements:          'Bank Statements',
  upload_bank_reconciliation:      'Bank Reconciliation',
  upload_bank_confirmations:       'Bank Confirmations',
  upload_ar_confirmations:         'AR Confirmations',
  upload_ap_confirmations:         'AP Confirmations (Vendor)',
  upload_inventory_confirmations:  'Inventory Confirmations',
  upload_inventory_observation:    'Physical Inventory Observation',
};

const WORKFLOW_DONE_EVENTS = new Set([
  'classification_complete','workflow_assigned','financial_analysis_complete',
  'balance_extraction_complete','balances_extracted','extract_balances_complete',
  'materiality_assessed','assess_materiality_complete',
  'trial_balance_uploaded','upload_trial_balance_complete',
  'income_statement_uploaded','upload_income_statement_complete',
  'bank_statements_uploaded','upload_bank_statements_complete',
  'bank_reconciliation_uploaded','upload_bank_reconciliation_complete',
  'bank_confirmations_uploaded','upload_bank_confirmations_complete',
  'ar_confirmations_uploaded','upload_ar_confirmations_complete',
  'ap_confirmations_uploaded','upload_ap_confirmations_complete',
  'inventory_confirmations_uploaded','upload_inventory_confirmations_complete',
  'inventory_observation_uploaded','upload_inventory_observation_complete',
  'assertions_tested','test_assertions_complete','substantive_testing_complete',
  'audit_procedures_formulated','financial_audit_testing_complete',
  'findings_generated','workpapers_generated',
  'supporting_document_uploaded','workflow_reset_for_retesting',
]);

const STARTER_PROMPTS = [
  "Set up a new audit engagement",
  "What's the next audit step?",
  "Run the next audit step for this document",
  "Run all remaining audit steps",
  "What's the current workflow status?",
  "What PBC documents do I need from the client?",
  "Draft a PBC client request letter",
  "Upload supporting documents",
  "Draft the management letter",
];

// Single source of truth for the initial chat state so the mount effect
// and the reset button can never drift apart.
const buildWelcome = (docId) => ([{
  id: 'welcome', from: 'bot',
  text: docId
    ? "Document selected — ask me what the next audit step is, or tell me to run it."
    : "Select a document row in the dashboard above, then I can drive the audit workflow.",
}]);

// Anomalies from a chain run carry a `source` (the stepId that produced them).
// Group them so each AnomalyEscalationCard tags its issues with the right step
// rather than lumping everything under a generic label.
const groupAnomaliesBySource = (anomalies = [], executedSteps = []) => {
  const titleByStepId = executedSteps.reduce((acc, s) => {
    if (s?.stepId) acc[s.stepId] = s.stepTitle || s.stepId;
    return acc;
  }, {});

  const groups = [];
  const index  = {};

  anomalies.forEach((a) => {
    const stepId = a.source || 'run_chain';
    if (index[stepId] === undefined) {
      index[stepId] = groups.length;
      groups.push({
        stepId,
        stepLabel: titleByStepId[stepId] || stepId.replace(/_/g, ' '),
        items: [],
      });
    }
    groups[index[stepId]].items.push(a);
  });

  return groups;
};

const RpaAgentChatLogic = ({ selectedDocId, selectedProjectId, onClose }) => {
  const [messages,        setMessages]        = useState([]);
  const [input,           setInput]           = useState('');
  const [loading,         setLoading]         = useState(false);
  const [pendingLogId,    setPendingLogId]    = useState(null);
  const [pendingPlan,     setPendingPlan]     = useState(null);
  const [approving,       setApproving]       = useState(false);
  const [pendingFileStep, setPendingFileStep] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [uploading,       setUploading]       = useState(false);
  const [resuming,        setResuming]        = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef   = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!selectedDocId) {
      setMessages(buildWelcome(null));
      setPendingFileStep(null); setSelectedDocType('');
      setPendingLogId(null); setPendingPlan(null);
      return;
    }
    setPendingFileStep(null); setSelectedDocType('');
    setPendingLogId(null); setPendingPlan(null);
    setMessages([{ id: 'loading-status', from: 'bot', text: 'Loading workflow status...' }]);
    autoLoadDocStatus(selectedDocId);
  }, [selectedDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedDocId) return;
    const socket = window.__RPA_SOCKET__;
    if (!socket) return;

    const eventName = `rpa_doc_${selectedDocId}`;

    const onDocUpdate = (evt) => {
      if (!evt?.type || !WORKFLOW_DONE_EVENTS.has(evt.type)) return;
      setPendingFileStep(null);
      setSelectedDocType('');

      if (evt.type === 'workflow_reset_for_retesting') {
        setMessages(prev => [...prev, {
          id: `socket-${Date.now()}`, from: 'bot',
          text: `📎 Supporting document uploaded — testing steps have been reset.\n\nTest Assertions, Substantive Testing, Audit Procedures, Audit Testing, and Generate Findings will need to re-run to incorporate the new document. Ask me to run the next step when ready.`,
        }]);
        return;
      }

      const label = evt.type.replace(/_/g, ' ');
      setMessages(prev => [...prev, {
        id: `socket-${Date.now()}`, from: 'bot',
        text: `✅ Dashboard update: **${label}** — ask me what the next step is.`,
      }]);
    };

    // Live chain progress. Backend emits 'rpa_agent_update' into doc:<docId>.
    const onAgentUpdate = (evt) => {
      if (!evt?.type) return;

      const setLive = (text) => setMessages(prev => ([
        ...prev.filter(m => m.id !== CHAIN_LIVE_ID),
        { id: CHAIN_LIVE_ID, from: 'bot', type: 'running', text },
      ]));

      if (evt.type === 'chain_step_start') {
        setLive(`⏳ ${evt.message || 'Running the next step…'}`);
        return;
      }
      if (evt.type === 'chain_step_complete') {
        const title = evt.data?.stepTitle || 'Step';
        const flagged = evt.data?.anomalyCount
          ? ` — ${evt.data.anomalyCount} item${evt.data.anomalyCount !== 1 ? 's' : ''} flagged`
          : '';
        setLive(`✅ ${title} complete${flagged} — continuing…`);
        return;
      }
      if (evt.type === 'chain_step_error' || evt.type === 'chain_finished') {
        setMessages(prev => prev.filter(m => m.id !== CHAIN_LIVE_ID));
      }
    };

    socket.on(eventName, onDocUpdate);
    socket.on('rpa_agent_update', onAgentUpdate);
    socket.emit('rpa_subscribe_docs', { docIds: [selectedDocId] });

    return () => {
      socket.off(eventName, onDocUpdate);
      socket.off('rpa_agent_update', onAgentUpdate);
    };
  }, [selectedDocId]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    setMessages(prev => [...prev, { id: Date.now(), from: 'user', text }]);
    setInput('');
    setLoading(true);

    // Client-side intercept — must come BEFORE docId check
    const lowerText = text.trim().toLowerCase();
    if (lowerText.includes('set up') || lowerText.includes('new audit') ||
        lowerText.includes('new engagement') || lowerText.includes('create audit') ||
        lowerText.includes('start audit')) {
      setMessages(prev => [...prev, { id: Date.now(), from: 'bot', type: 'setup_engagement' }]);
      setLoading(false);
      return;
    }

    if (!selectedDocId) {
      setMessages(prev => [...prev, { id: Date.now() + 1, from: 'bot',
        text: "Please select a document row in the dashboard first, or say 'Set up a new audit engagement' to create one." }]);
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/admin/rpa/agent/command`,
        { message: text.trim(), docId: selectedDocId, projectId: selectedProjectId, mode: 'approval_required' },
        { withCredentials: true }
      );
      handleAgentResponse(data);
    } catch (err) {
      setMessages(prev => [...prev, { id: Date.now() + 1, from: 'bot',
        text: `Something went wrong: ${err.response?.data?.error || err.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  // Fully reset the chat back to its initial mounted state:
  // welcome message restored, starter prompts reappear (messages.length <= 1),
  // and any pending approval / file-upload state is cleared.
  const resetChat = useCallback(() => {
    setMessages(buildWelcome(selectedDocId));
    setInput('');
    setPendingLogId(null);
    setPendingPlan(null);
    setPendingFileStep(null);
    setSelectedDocType('');
  }, [selectedDocId]);

  const autoLoadDocStatus = async (docId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/rpa/docs`, { withCredentials: true });
      const raw  = res.data;
      const docs = raw?.items || raw?.docs || raw?.data || (Array.isArray(raw) ? raw : []);
      const doc  = docs.find(d => String(d._id) === String(docId) || String(d.id) === String(docId));
      if (doc) {
        const allDone = doc?.workflowAssignment?.steps?.length > 0 &&
                        doc?.workflowAssignment?.steps?.every(s => s.status === 'completed');
        setMessages([
          { id: 'status-card', from: 'bot', type: 'workflow_status', doc, docId },
          { id: 'status-hint', from: 'bot',
            text: allDone
              ? 'All steps complete - ask me to draft the management letter, or click Anomalies on any step above.'
              : "Ask me what the next audit step is, or tell me to run it." },
        ]);
      } else { setMessages(buildWelcome(docId)); }
    } catch (_) { setMessages(buildWelcome(docId)); }
  };

  const handleAgentResponse = (data) => {
    if (!data.ok) {
      setMessages(prev => [
        ...prev.filter(m => m.id !== CHAIN_LIVE_ID),
        { id: Date.now(), from: 'bot', text: data.error || data.message || 'An error occurred.' },
      ]);
      return;
    }
    switch (data.type) {
      case 'approval_required':
        setPendingLogId(data.logId);
        setPendingPlan(data.plan);
        setMessages(prev => [...prev, { id: Date.now(), from: 'bot', type: 'approval', plan: data.plan, logId: data.logId }]);
        break;
      case 'result':
        setPendingLogId(null); setPendingPlan(null); setPendingFileStep(null);
        setMessages(prev => [...prev, { id: Date.now(), from: 'bot', type: 'result',
          plan: data.plan, summary: data.summary, nextStep: data.nextStep,
          anomalies: data.anomalies || [], docId: selectedDocId }]);
        break;
      case 'chain': {
        setPendingLogId(null); setPendingPlan(null);

        // If the chain stopped waiting on a file, arm the in-chat uploader so
        // the Upload button on the card has somewhere to land.
        const stopped = data.stoppedAt || {};
        if (stopped.reason === 'requires_file' && stopped.stepId) {
          setPendingFileStep({
            stepId:    stopped.stepId,
            stepLabel: FILE_STEP_LABELS[stopped.stepId] || stopped.stepTitle || stopped.stepId,
          });
        } else {
          setPendingFileStep(null);
        }

        setMessages(prev => [
          ...prev.filter(m => m.id !== CHAIN_LIVE_ID),
          {
            id: Date.now(), from: 'bot', type: 'chain',
            chain: data,
            anomalies: data.anomalies || [],
            docId: selectedDocId,
          },
        ]);
        break;
      }
      case 'status':
        setMessages(prev => [...prev, { id: Date.now(), from: 'bot', text: buildStatusText(data) }]);
        break;
      case 'complete':
        setMessages(prev => [...prev, { id: Date.now(), from: 'bot', text: '🎉 All workflow steps are complete for this document.' }]);
        break;
      case 'setup_engagement':
        setMessages(prev => [...prev, { id: Date.now(), from: 'bot', type: 'setup_engagement' }]);
        break;
      case 'pbc_preview':
        setMessages(prev => [...prev, {
          id: Date.now(), from: 'bot', type: 'pbc_preview',
          docId: data.docId, docName: data.docName, clientName: data.clientName,
          pendingItems: data.pendingItems, text: data.message,
        }]);
        break;
      case 'management_report':
        setMessages(prev => [...prev, {
          id: Date.now(), from: 'bot', type: 'management_report',
          docId: data.docId, docName: data.docName, clientName: data.clientName,
          report: data.report,
        }]);
        break;
      case 'supporting_docs':
        // Optional step — arm the in-chat uploader immediately. The doc-type
        // dropdown comes from FILE_UPLOAD_ENDPOINTS.needsDocType.
        setPendingFileStep({
          stepId:    'upload_supporting_documents',
          stepLabel: 'Supporting Document',
        });
        setMessages(prev => [...prev, {
          id: Date.now(), from: 'bot', type: 'supporting_docs',
          docId: data.docId, docName: data.docName,
          uploaded: data.uploaded || [], available: data.available || [],
          totalFlags: data.totalFlags || 0,
          resetsDownstream: !!data.resetsDownstream,
          text: data.message,
        }]);
        break;
      case 'requires_file': {
        const stepId    = data.stepId;
        const stepLabel = FILE_STEP_LABELS[stepId] || stepId?.replace(/_/g, ' ') || 'required file';
        setPendingFileStep({ stepId, stepLabel });
        setMessages(prev => [...prev, { id: Date.now(), from: 'bot', type: 'requires_file', stepId, stepLabel,
          text: `The next step is **${stepLabel}** — upload the file below or use the dashboard button.` }]);
        break;
      }
      default:
        setMessages(prev => [...prev, { id: Date.now(), from: 'bot', text: data.message || 'An unexpected error occurred.' }]);
    }
  };

  // Re-runs the chain from wherever it stopped. The backend resolves the next
  // pending step from stored data, so this is safe to call repeatedly.
  const handleResumeChain = async () => {
    if (!selectedDocId || resuming) return;
    setResuming(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/rpa/agent/command`,
        { message: 'resume chain', docId: selectedDocId, projectId: selectedProjectId, mode: 'auto' },
        { withCredentials: true }
      );
      handleAgentResponse(data);
    } catch (err) {
      setMessages(prev => [...prev, { id: Date.now(), from: 'bot',
        text: `Could not resume: ${err.response?.data?.error || err.message}` }]);
    } finally {
      setResuming(false);
    }
  };

  const handleChainUploadNeeded = (stepId, stepTitle) => {
    if (!stepId) return;
    setPendingFileStep({
      stepId,
      stepLabel: FILE_STEP_LABELS[stepId] || stepTitle || stepId.replace(/_/g, ' '),
    });
  };

  const handleFileUpload = async (file) => {
    if (!file || !pendingFileStep || !selectedDocId) return;
    const uploadDef = FILE_UPLOAD_ENDPOINTS[pendingFileStep.stepId];
    if (!uploadDef) {
      setMessages(prev => [...prev, { id: Date.now(), from: 'bot',
        text: 'Upload not supported here for this step — use the dashboard button.' }]);
      return;
    }

    let resolvedEndpoint = uploadDef.endpoint.replace('{docId}', selectedDocId);
    if (uploadDef.needsDocType) {
      const docType = selectedDocType || 'additional_evidence';
      resolvedEndpoint = resolvedEndpoint.replace('{docType}', docType);
    }
    const url = `${API_URL}${resolvedEndpoint}`;
    setUploading(true);
    const uploadMsgId = `upload-${Date.now()}`;
    setMessages(prev => [...prev, { id: uploadMsgId, from: 'bot', type: 'uploading',
      text: `⏳ Uploading **${file.name}**…` }]);

    try {
      const formData = new FormData();
      formData.append(uploadDef.fieldName, file);
      formData.append('docId', selectedDocId);
      await axios.post(url, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });

      setPendingFileStep(null);
      setSelectedDocType('');
      setMessages(prev => [
        ...prev.filter(m => m.id !== uploadMsgId),
        {
          id: Date.now(), from: 'bot',
          text: `✅ **${file.name}** uploaded successfully. The dashboard is refreshing — ask me what the next step is when ready, or click ↻ to check.`,
        },
      ]);
    } catch (err) {
      setMessages(prev => [
        ...prev.filter(m => m.id !== uploadMsgId),
        { id: Date.now(), from: 'bot', text: `Upload failed: ${err.response?.data?.error || err.message}` },
      ]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleApprove = async () => {
    if (!pendingLogId || approving) return;
    setApproving(true);
    setMessages(prev => prev.map(m =>
      m.logId === pendingLogId ? { ...m, type: 'running', text: `⏳ Running **${pendingPlan?.description}**…` } : m
    ));
    try {
      const { data } = await axios.post(`${API_URL}/admin/rpa/agent/approve`,
        { logId: pendingLogId }, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.logId !== pendingLogId));
      setPendingLogId(null); setPendingPlan(null);
      handleAgentResponse(data);
    } catch (err) {
      setMessages(prev => [...prev, { id: Date.now(), from: 'bot',
        text: `Execution failed: ${err.response?.data?.error || err.message}` }]);
      setPendingLogId(null); setPendingPlan(null);
    } finally {
      setApproving(false);
    }
  };

  const handleCancel = () => {
    setMessages(prev => [...prev.filter(m => m.logId !== pendingLogId),
      { id: Date.now(), from: 'bot', text: "Cancelled. Let me know when you're ready to continue." }]);
    setPendingLogId(null); setPendingPlan(null);
  };

  const handleFileCancel = () => {
    setPendingFileStep(null);
    setSelectedDocType('');
  };

  const buildStatusText = (data) => {
    if (data.nextStep?.status === 'complete') return `✅ All ${data.total} workflow steps complete.`;
    if (data.nextStep?.status === 'no_workflow') return `No workflow assigned yet.`;
    const pct = data.total ? Math.round((data.completed / data.total) * 100) : 0;
    return `📊 **${data.docName}** (${data.workflow})\n${data.completed}/${data.total} steps (${pct}%)\nNext: **${data.nextStep?.stepTitle || data.nextStep?.stepId}**`;
  };

  const renderMessage = (msg) => {
    if (msg.type === 'setup_engagement') {
      return (
        <div key={msg.id} className="agent-msg bot">
          <FaRobot className="agent-robot-icon" />
          <div className="agent-msg-content" style={{ width: '100%' }}>
            <EngagementSetupCard
              onComplete={({ docId, projectId, docName, clientName, templateId }) => {
                setMessages(prev => [...prev, {
                  id: Date.now(), from: 'bot',
                  text: `✅ **${docName}** set up under ${clientName}. Initial AI steps complete.`,
                }]);
                if (window.__RPA_DISPATCH__) {
                  window.__RPA_DISPATCH__({ type: 'SET_RPA_AGENT_CONTEXT', payload: { selectedDocId: docId, selectedProjectId: projectId, selectedDocName: docName, selectedWorkflow: templateId } });
                }
              }}
              onCancel={() => {
                setMessages(prev => [...prev, { id: Date.now(), from: 'bot',
                  text: "Cancelled. Let me know when you're ready to set up the engagement." }]);
              }}
            />
          </div>
        </div>
      );
    }
    if (msg.type === 'workflow_status')
      return (
        <div key={msg.id} className="agent-msg bot">
          <FaRobot className="agent-robot-icon" />
          <div className="agent-msg-content" style={{ width: '100%' }}>
            <WorkflowStatusCard doc={msg.doc} docId={msg.docId} />
          </div>
        </div>
      );
    if (msg.type === 'approval')
      return (
        <div key={msg.id} className="agent-msg bot">
          <FaRobot className="agent-robot-icon" />
          <div className="agent-msg-content">
            <AgentApprovalCard plan={msg.plan} onApprove={handleApprove} onCancel={handleCancel} loading={approving} />
          </div>
        </div>
      );
    if (msg.type === 'result')
      return (
        <div key={msg.id} className="agent-msg bot">
          <FaRobot className="agent-robot-icon" />
          <div className="agent-msg-content" style={{ width: '100%' }}>
            <AgentResultCard plan={msg.plan} summary={msg.summary} nextStep={msg.nextStep} />
            {msg.anomalies?.length > 0 && (
              <AnomalyEscalationCard
                docId={msg.docId || selectedDocId}
                stepId={msg.plan?.stepId}
                stepLabel={msg.plan?.stepTitle || msg.plan?.stepId}
                anomalies={msg.anomalies}
              />
            )}
          </div>
        </div>
      );
    if (msg.type === 'chain') {
      const groups = groupAnomaliesBySource(msg.anomalies, msg.chain?.executedSteps);
      return (
        <div key={msg.id} className="agent-msg bot">
          <FaRobot className="agent-robot-icon" />
          <div className="agent-msg-content" style={{ width: '100%' }}>
            <BatchProgressCard
              chain={msg.chain}
              onUploadNeeded={handleChainUploadNeeded}
              onResume={handleResumeChain}
              resuming={resuming}
            />
            {groups.map(g => (
              <AnomalyEscalationCard
                key={g.stepId}
                docId={msg.docId || selectedDocId}
                stepId={g.stepId}
                stepLabel={g.stepLabel}
                anomalies={g.items}
              />
            ))}
          </div>
        </div>
      );
    }
    if (msg.type === 'pbc_preview') {
      return (
        <div key={msg.id} className="agent-msg bot">
          <FaRobot className="agent-robot-icon" />
          <div className="agent-msg-content" style={{ width: '100%' }}>
            {!msg.pendingItems?.length
              ? <span className="agent-msg-text">✅ All required documents have already been uploaded for this engagement.</span>
              : <PBCLetterCard docId={msg.docId} docName={msg.docName} clientName={msg.clientName} pendingItems={msg.pendingItems} />
            }
          </div>
        </div>
      );
    }
    if (msg.type === 'management_report') {
      return (
        <div key={msg.id} className="agent-msg bot">
          <FaRobot className="agent-robot-icon" />
          <div className="agent-msg-content" style={{ width: '100%' }}>
            <ManagementReportCard docId={msg.docId} docName={msg.docName} clientName={msg.clientName} initialReport={msg.report} />
          </div>
        </div>
      );
    }
    if (msg.type === 'supporting_docs') {
      return (
        <div key={msg.id} className="agent-msg bot">
          <FaRobot className="agent-robot-icon" />
          <div className="agent-msg-content" style={{ width: '100%' }}>
            <span className="agent-msg-text">{msg.text}</span>

            {msg.uploaded.length > 0 && (
              <div className="agent-supporting-list">
                {msg.uploaded.map(u => (
                  <div
                    key={u.docType}
                    className={`agent-supporting-item${u.auditFlagCount > 0 ? ' flagged' : ''}`}
                  >
                    <span className="agent-supporting-name">{u.displayName}</span>
                    {u.auditFlagCount > 0
                      ? <span className="agent-supporting-flags">⚠️ {u.auditFlagCount} flag{u.auditFlagCount !== 1 ? 's' : ''}</span>
                      : <span className="agent-supporting-clean">✓ clear</span>}
                  </div>
                ))}
              </div>
            )}

            {msg.resetsDownstream && (
              <div className="agent-supporting-warning">
                ⚠️ Testing has already run on this document. Uploading another supporting
                document resets Test Assertions through Generate Findings so the new
                evidence is incorporated — you'll need to re-run those steps.
              </div>
            )}
          </div>
        </div>
      );
    }
    if (msg.type === 'running' || msg.type === 'uploading')
      return (
        <div key={msg.id} className="agent-msg bot running">
          <FaRobot className="agent-robot-icon spinning" />
          <div className="agent-msg-content"><span className="agent-msg-text">{msg.text}</span></div>
        </div>
      );
    return (
      <div key={msg.id} className={`agent-msg ${msg.from}`}>
        {msg.from === 'bot' && <FaRobot className="agent-robot-icon" />}
        <div className="agent-msg-content"><span className="agent-msg-text">{msg.text}</span></div>
      </div>
    );
  };

  return (
    <div className="rpa-agent-chat">
      <div className="agent-chat-header">
        <div className="agent-header-left">
          <FaRobot className="agent-header-icon" />
          <div>
            <div className="agent-header-title">RPA Audit Agent</div>
            <div className="agent-header-sub">{selectedDocId ? '● Document selected' : '○ No document selected'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            className="agent-refresh-btn"
            onClick={resetChat}
            disabled={loading || approving || uploading || resuming}
            title="Reset chat"
          >↻</button>
          {onClose && <button className="agent-close-btn" onClick={onClose}>✕</button>}
        </div>
      </div>

      <div className="agent-messages">
        {messages.map(renderMessage)}
        {loading && (
          <div className="agent-msg bot">
            <FaRobot className="agent-robot-icon" />
            <div className="agent-msg-content"><div className="agent-typing"><span /><span /><span /></div></div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {pendingFileStep && (
        <div className="agent-file-upload-row">
          <FaPaperclip className="agent-file-icon" />
          <span className="agent-file-label">Upload {pendingFileStep.stepLabel}</span>
          {FILE_UPLOAD_ENDPOINTS[pendingFileStep.stepId]?.needsDocType && (
            <select
              className="agent-doctype-select"
              value={selectedDocType}
              onChange={e => setSelectedDocType(e.target.value)}
              disabled={uploading}
            >
              <option value="">— Select doc type —</option>
              {SUPPORTING_DOC_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          )}
          <input
            ref={fileInputRef}
            type="file"
            id="agent-file-input"
            className="agent-file-input-hidden"
            disabled={uploading || (FILE_UPLOAD_ENDPOINTS[pendingFileStep.stepId]?.needsDocType && !selectedDocType)}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
          />
          <label
            htmlFor="agent-file-input"
            className={`agent-file-btn${(uploading || (FILE_UPLOAD_ENDPOINTS[pendingFileStep.stepId]?.needsDocType && !selectedDocType)) ? ' disabled' : ''}`}
            title={FILE_UPLOAD_ENDPOINTS[pendingFileStep.stepId]?.needsDocType && !selectedDocType ? 'Select a document type first' : undefined}
          >
            {uploading ? 'Uploading…' : 'Choose File'}
          </label>
          <button className="agent-file-cancel" onClick={handleFileCancel} disabled={uploading}>✕</button>
        </div>
      )}

      {messages.length <= 1 && (
        <div className="agent-starters">
          {STARTER_PROMPTS.map((p, i) => (
            <button key={i} className="agent-starter-btn" onClick={() => sendMessage(p)} disabled={loading}>{p}</button>
          ))}
        </div>
      )}

      <div className="agent-input-row">
        <input
          type="text"
          className="agent-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && sendMessage(input)}
          placeholder={selectedDocId ? 'Ask about the next audit step…' : 'Select a document first…'}
          disabled={loading || approving || uploading || resuming}
        />
        <button className="agent-send-btn"
          onClick={() => sendMessage(input)}
          disabled={loading || approving || uploading || resuming || !input.trim()}>
          Send
        </button>
      </div>
    </div>
  );
};

export default RpaAgentChatLogic;